<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { useMobileDialogProps } from '@/composables/useMobileDialogProps'
import { useReducedMotion } from '@/composables/useReducedMotion'
import type { InstallPlatform } from '@/utils/platform'

const props = defineProps<{
  modelValue: boolean
  platform: InstallPlatform
  canInstall: boolean
  install: () => Promise<boolean>
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  installed: []
  'remind-later': []
  'dont-remind': []
}>()

interface Guide {
  icon: string
  steps: string[]
}

const guides: Record<InstallPlatform, Guide> = {
  ios: {
    icon: 'mdi-apple',
    steps: [
      'Tap the Share button in the Safari toolbar.',
      'Choose "Add to Home Screen" and tap "Add".',
    ],
  },
  android: {
    icon: 'mdi-android',
    steps: [
      'Open the browser menu (three dots) in the top right.',
      'Choose "Install app" or "Add to Home screen" and confirm.',
    ],
  },
  desktop: {
    icon: 'mdi-monitor',
    steps: [
      'Click the install icon in the address bar, or open the browser menu.',
      'Choose "Install Fam-Bam". No install option (e.g. Firefox)? Use Chrome or Edge.',
    ],
  },
  generic: {
    icon: 'mdi-cellphone-link',
    steps: [
      'Open your browser menu.',
      'Choose "Install app" or "Add to Home Screen" and follow the prompts.',
    ],
  },
}

const guide = computed<Guide>(() => guides[props.platform] ?? guides.generic)
const showInstallButton = computed(() => props.canInstall && props.platform !== 'ios')
const isInstalling = ref(false)
const installError = ref(false)
const { xs } = useDisplay()
const theme = useTheme()
const reducedMotion = useReducedMotion()
const titleId = useId()

watch(() => props.modelValue, (isVisible) => {
  if (isVisible) installError.value = false
})

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    // Scrim / Escape close counts as the softer "remind me later".
    if (!value) {
      remindLater()
      return
    }
    emit('update:modelValue', value)
  },
})

const mobileDialogProps = useMobileDialogProps(isOpen)
const dialogProps = computed(() => ({
  ...mobileDialogProps.value,
  ...(reducedMotion.value ? { transition: false as const } : {}),
}))

function remindLater(): void {
  emit('update:modelValue', false)
  emit('remind-later')
}

function dontRemind(): void {
  emit('update:modelValue', false)
  emit('dont-remind')
}

async function onInstall(): Promise<void> {
  if (isInstalling.value) return // guard against double-invoke while native prompt is pending
  installError.value = false
  isInstalling.value = true
  try {
    const accepted = await props.install()
    if (accepted) {
      emit('update:modelValue', false)
      emit('installed')
    }
    // Cancelled: keep the dialog open so the user can still pick a reminder option.
  } catch {
    installError.value = true
  } finally {
    isInstalling.value = false
  }
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    max-width="480"
    data-testid="install-dialog"
    v-bind="dialogProps"
  >
    <v-card
      class="install-card"
      :class="{ 'install-card--dark': theme.current.value.dark }"
      :aria-labelledby="titleId"
    >
      <v-card-title class="install-header pa-6 pb-4">
        <v-sheet
          class="install-tile mx-auto mb-4"
          :class="{ 'install-tile--bounce': !reducedMotion }"
          data-testid="install-dialog-tile"
          aria-hidden="true"
        >
          <v-icon icon="mdi-home-heart" size="52" />
          <v-avatar class="install-tile-star" size="32">
            <v-icon icon="mdi-star" size="24" />
          </v-avatar>
        </v-sheet>
        <v-label :id="titleId" class="install-title">Install Fam-Bam</v-label>
        <v-btn
          class="install-close"
          icon="mdi-close"
          variant="text"
          width="44"
          height="44"
          aria-label="Close"
          data-testid="install-dialog-close"
          @click="remindLater"
        />
      </v-card-title>

      <v-card-text class="px-6">
        <v-sheet color="transparent" class="text-body-1 text-medium-emphasis text-center mb-4">
          A home-screen icon, full-screen view, and quicker access.
        </v-sheet>
        <v-sheet color="transparent" class="d-flex align-center mb-2">
          <v-icon :icon="guide.icon" size="32" color="primary" class="me-2" />
          <v-label class="text-subtitle-1 font-weight-medium">How to install</v-label>
        </v-sheet>
        <v-list density="compact" data-testid="install-dialog-steps">
          <v-list-item
            v-for="(item, index) in guide.steps"
            :key="index"
            :prepend-icon="`mdi-numeric-${index + 1}-circle-outline`"
          >
            <v-list-item-title class="text-body-2 text-wrap">{{ item }}</v-list-item-title>
          </v-list-item>
        </v-list>
        <v-alert
          v-if="installError"
          type="error"
          density="compact"
          class="mt-2"
          data-testid="install-dialog-error"
        >Unable to install right now. Please try again.</v-alert>
      </v-card-text>

      <v-card-actions class="install-actions px-6 pt-4 pb-2" :class="{ 'flex-column-reverse align-stretch': xs }">
        <v-spacer v-if="!xs" />
        <v-btn
          variant="text"
          color="primary"
          height="52"
          :block="xs"
          data-testid="install-dialog-remind-later"
          @click="remindLater"
        >
          Not now
        </v-btn>
        <v-btn
          v-if="showInstallButton"
          color="primary"
          variant="flat"
          class="fb-chunky install-confirm"
          height="52"
          :block="xs"
          :loading="isInstalling"
          :disabled="isInstalling"
          data-testid="install-dialog-install"
          @click="onInstall"
        >
          Install
        </v-btn>
      </v-card-actions>
      <v-card-actions class="justify-center px-6 pt-0 pb-4">
        <v-btn
          variant="text"
          class="text-medium-emphasis"
          height="44"
          data-testid="install-dialog-dont-remind"
          @click="dontRemind"
        >
          Don't remind me again
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.install-card {
  --tile-bg: #6C3DDB;
  --tile-ink: #FFFFFF;
  --star-bg: #FFE5A0;
  --star-ink: #6B4700;
  border-radius: 28px;
  color: rgb(var(--v-theme-on-surface));
}

.install-card--dark {
  --tile-bg: #8B5CF6;
  --star-bg: #F7D477;
  --star-ink: #503500;
}

.install-header {
  position: relative;
  text-align: center;
  white-space: normal;
}

.install-tile {
  position: relative;
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  border-radius: 26px;
  background: var(--tile-bg);
  color: var(--tile-ink);
}

.install-tile-star {
  position: absolute;
  right: -8px;
  bottom: -4px;
  background: var(--star-bg);
  color: var(--star-ink);
}

.install-tile--bounce {
  animation: install-tile-bounce 1.6s ease-in-out 3;
}

.install-title {
  display: block;
  white-space: normal;
  opacity: 1;
  font-family: var(--fb-font-display, 'Bricolage Grotesque', sans-serif);
  font-size: 30px;
  font-weight: 800;
  line-height: 1.05;
}

.install-close {
  position: absolute;
  top: 16px;
  right: 16px;
}

.install-actions .install-confirm {
  border-radius: 18px;
  font-size: 18px;
  font-weight: 900;
}

.install-card :deep(.v-btn:focus-visible) {
  outline: 3px solid rgb(var(--v-theme-on-surface));
  outline-offset: 2px;
}

@keyframes install-tile-bounce {
  0%, 40%, 100% { transform: translateY(0); }
  20% { transform: translateY(-10px); }
  30% { transform: translateY(-4px); }
}

@media (prefers-reduced-motion: reduce) {
  .install-tile--bounce {
    animation: none !important;
  }

  .install-card :deep(*) {
    animation: none !important;
    transition: none !important;
  }

  .install-actions .install-confirm:active {
    transform: none !important;
  }
}
</style>
